/**
 * api-client.ts — chatter-app local API surface (Phase 12-02).
 *
 * The CORE chat data layer (fetchJson primitive, authApi, inboxApi, conversationsApi,
 * messagesApi, fansApi, vaultApi, creatorsApi, chattersApi + their DTOs) was relocated
 * VERBATIM into @crm/chat (packages/chat/src/lib/api-client.ts) so the admin all-chats
 * surface (Phase 12-03) can reuse it — see the re-exports below.
 *
 * Chatter-only clients (operatorsApi self-service lifecycle, transactionsApi Surface B,
 * broadcastsApi mass-messages) stay HERE, built on the get/post/put/patch/del primitives
 * re-exported from @crm/chat, so @crm/chat never imports them (Phase 12-02 design note —
 * these are supplied to ChatWorkspace/FanPanel as injected slots instead).
 */

import type {
  AcceptInvitationDto,
  RequestPasswordResetDto,
  CompletePasswordResetDto,
  ChangePasswordDto,
  EditDisplayNameDto,
  MeDto,
} from '@crm/types';
import {
  get,
  post,
  patch,
  ApiRequestError,
  authApi,
  inboxApi,
  conversationsApi,
  messagesApi,
  fansApi,
  vaultApi,
  creatorsApi,
  chattersApi,
  type InboxRefreshResult,
  type InboxFilter,
  type InboxFilterMode,
  type ConversationDetailDto,
  type MessageMediaDto,
  type MessageDto,
  type PendingSendState,
  type PendingSendDto,
  type MessagesListDto,
  type ConversationClaimDto,
  type ResolveConversationResultDto,
  type TranslatePreviewDto,
  type SendResultDto,
  type FanProfileDto,
  type VaultThumbnailDto,
  type VaultFolderDto,
  type VaultFolderPageDto,
  type VaultMediaItemDto,
  type VaultMediaPageDto,
  type CreatorAccountSummary,
  type CreatorRollupDto,
  type ChatterDto,
  broadcastsApi,
  transactionsApi,
  type CampaignDto,
  type CreateCampaignBody,
  type MaloumListDto,
  type ChatterTransactionDto,
  type ChatterTransactionPageDto,
} from '@crm/chat';

export {
  ApiRequestError,
  authApi,
  inboxApi,
  conversationsApi,
  messagesApi,
  fansApi,
  vaultApi,
  creatorsApi,
  chattersApi,
  type InboxRefreshResult,
  type InboxFilter,
  type InboxFilterMode,
  type ConversationDetailDto,
  type MessageMediaDto,
  type MessageDto,
  type PendingSendState,
  type PendingSendDto,
  type MessagesListDto,
  type ConversationClaimDto,
  type ResolveConversationResultDto,
  type TranslatePreviewDto,
  type SendResultDto,
  type FanProfileDto,
  type VaultThumbnailDto,
  type VaultFolderDto,
  type VaultFolderPageDto,
  type VaultMediaItemDto,
  type VaultMediaPageDto,
  type CreatorAccountSummary,
  type CreatorRollupDto,
  type ChatterDto,
  // Broadcasts (mass messages) + Chatter Transactions (Surface B) were relocated into @crm/chat
  // (2026-07-20, shared with the admin full-parity chat surface). Re-exported here so existing
  // chatter call sites (BroadcastsModal, TransactionsPane, ChatterWorkspace) are unchanged.
  broadcastsApi,
  transactionsApi,
  type CampaignDto,
  type CreateCampaignBody,
  type MaloumListDto,
  type ChatterTransactionDto,
  type ChatterTransactionPageDto,
};

// ---------------------------------------------------------------------------
// Operators — self-service lifecycle (Phase 9 SC-1/4/5)
//
// Chatter surface exposes only the self-service + public routes:
//   GET  /users/me                      — self-service profile
//   PATCH /users/me/password            — change own password
//   PATCH /users/me/display-name        — edit own display name
//   POST  /users/accept-invitation      — public; accept invite
//   POST  /users/reset-password/request — public; forgot-password (always 200)
//   POST  /users/reset-password/complete — public; consume token
//
// Admin-only routes (invite/list/revoke/remove) are NOT exposed here.
// ---------------------------------------------------------------------------

export const operatorsApi = {
  /** GET /users/me — self-service profile (any authenticated). */
  me: () => get<MeDto>('/users/me'),

  /** PATCH /users/me/password — change own password (any authenticated). */
  changePassword: (dto: ChangePasswordDto) =>
    patch<void>('/users/me/password', dto),

  /** PATCH /users/me/display-name — edit own display name (any authenticated). */
  editDisplayName: (dto: EditDisplayNameDto) =>
    patch<void>('/users/me/display-name', dto),

  /** POST /users/accept-invitation — public; accept invite + set pw + display name. */
  acceptInvitation: (dto: AcceptInvitationDto) =>
    post<void>('/users/accept-invitation', dto),

  /** POST /users/reset-password/request — public; always 200 (non-enumerating, T-09-20). */
  requestReset: (dto: RequestPasswordResetDto) =>
    post<void>('/users/reset-password/request', dto),

  /** POST /users/reset-password/complete — public; consume token + set new password. */
  completeReset: (dto: CompletePasswordResetDto) =>
    post<void>('/users/reset-password/complete', dto),
};
